import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MapPin, Heart, Users, MessageCircle, Sparkles, ArrowRight, PartyPopper } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * WelcomeUnlockedModal — iter195
 *
 * Fires ONCE per account the moment a fresh signup lands on any page
 * with `?welcome=1` (Register.jsx redirects there). Renders a 30-second
 * confetti storm, a "Here's what you just unlocked" perks list, and a
 * big green "Start exploring" CTA. Uses only CSS keyframes (no new deps).
 *
 * Auto-dismisses after 30s. Also dismissable by CTA or ESC. First open
 * POSTs /auth/mark-welcome-seen so a mid-animation refresh doesn't loop
 * the celebration. Silent for signed-out users, silent if the account
 * already has `welcome_seen_at` set server-side.
 */
const CONFETTI_COUNT = 90;
const AUTO_DISMISS_MS = 30_000;
const PIECE_COLORS = ["#ff4500", "#0a7f3f", "#fbbf24", "#3b82f6", "#a855f7", "#ff8a3d"];

export default function WelcomeUnlockedModal() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const stampedRef = useRef(false);

  const wantsWelcome = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get("welcome") === "1";
  }, [location.search]);

  // Fire the modal only when: page has ?welcome=1, we have a signed-in
  // user, and the user has NEVER seen the welcome before.
  useEffect(() => {
    if (loading || !user || !wantsWelcome) return undefined;
    if (user.welcome_seen_at) return undefined;
    setOpen(true);
    // Stamp on the server so a refresh mid-animation doesn't re-loop.
    if (!stampedRef.current) {
      stampedRef.current = true;
      api.post("/auth/mark-welcome-seen").catch(() => { /* silent */ });
    }
    timerRef.current = setTimeout(() => setOpen(false), AUTO_DISMISS_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [loading, user, wantsWelcome]);

  // Strip ?welcome=1 from URL once the modal closes so a bounce back to
  // the page later doesn't try to fire it again for an old logged-in
  // account (belt-and-braces alongside the server-side stamp).
  useEffect(() => {
    if (open) return;
    if (!wantsWelcome) return;
    const q = new URLSearchParams(location.search);
    q.delete("welcome");
    const next = location.pathname + (q.toString() ? `?${q.toString()}` : "");
    navigate(next, { replace: true });
  }, [open, wantsWelcome, location.pathname, location.search, navigate]);

  // ESC closes
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const firstName = (user?.name || "").split(" ")[0] || "camper";

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-in fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-unlocked-title"
      data-testid="welcome-unlocked-modal"
    >
      <Confetti />
      <div className="relative bg-white w-full max-w-md sm:max-w-lg border-2 border-[#0e0e0e] shadow-[0_8px_0_0_#0e0e0e] overflow-hidden">
        <div className="bg-gradient-to-br from-[#ff4500] via-[#ff6a2b] to-[#ffa66c] text-white px-5 pt-5 pb-6 sm:px-7 sm:pt-6 sm:pb-8 relative overflow-hidden">
          <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-2 py-0.5 text-[10px] mono uppercase tracking-widest font-black mb-3">
            <PartyPopper className="w-3 h-3" /> Welcome aboard
          </div>
          <h2
            id="welcome-unlocked-title"
            className="font-display text-2xl sm:text-3xl leading-tight mb-2"
            data-testid="welcome-unlocked-title"
          >
            You&rsquo;re in, {firstName}!<br />Here&rsquo;s what just unlocked.
          </h2>
          <p className="text-white/95 text-sm sm:text-base leading-snug">
            Every feature on the app is now yours — free while we&rsquo;re growing. Early members like you get to keep this access when paid tiers arrive.
          </p>
        </div>

        <div className="px-5 sm:px-7 py-4 sm:py-5 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          <Perk icon={MapPin} text="Real prices, full map, all filters" />
          <Perk icon={Heart}  text="Save unlimited favourites" />
          <Perk icon={Users}  text="DMs + Campfire Chat" />
          <Perk icon={MessageCircle} text="Post to the Trip Wall + Community" />
          <Perk icon={Sparkles} text="Weather + price alerts" />
          <Perk icon={PartyPopper} text="Early-member perks kept when paid tiers land" />
        </div>

        <div className="px-5 sm:px-7 pb-5 sm:pb-6 pt-1 flex flex-col gap-2">
          <Link
            to="/browse"
            onClick={() => setOpen(false)}
            className="w-full inline-flex items-center justify-center gap-1.5 bg-[#0a7f3f] text-white font-black text-sm uppercase tracking-widest px-4 py-3 shadow-[0_4px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#111] transition-transform"
            data-testid="welcome-unlocked-cta"
          >
            Start exploring <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-[10px] text-[#999] hover:text-[#111] uppercase tracking-widest py-0.5"
            data-testid="welcome-unlocked-close"
          >
            Close this &amp; take my time
          </button>
        </div>
      </div>
    </div>
  );
}

function Perk({ icon: Icon, text }) {
  return (
    <div className="flex items-start gap-2 text-xs text-[#333]">
      <div className="w-6 h-6 shrink-0 bg-[#fff4ec] text-[#ff4500] flex items-center justify-center">
        <Icon className="w-3 h-3" strokeWidth={2.25} />
      </div>
      <span className="pt-0.5 leading-snug">{text}</span>
    </div>
  );
}

/* --------------------------- CSS confetti storm --------------------------- */
function Confetti() {
  const pieces = useMemo(() => {
    const list = [];
    for (let i = 0; i < CONFETTI_COUNT; i += 1) {
      list.push({
        id: i,
        left: Math.random() * 100,             // vw
        delay: Math.random() * 8,              // s — spread the storm across 30s
        duration: 4 + Math.random() * 5,       // s
        size: 5 + Math.random() * 6,           // px
        rotate: Math.random() * 360,           // deg initial
        drift: (Math.random() - 0.5) * 60,     // vw horizontal drift
        color: PIECE_COLORS[i % PIECE_COLORS.length],
        shape: i % 3 === 0 ? "circle" : "rect",
      });
    }
    return list;
  }, []);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[91] overflow-hidden"
      data-testid="welcome-unlocked-confetti"
    >
      <style>{CONFETTI_CSS}</style>
      {pieces.map((p) => (
        <span
          key={p.id}
          className={`cc-confetti-piece ${p.shape === "circle" ? "cc-confetti-circle" : ""}`}
          style={{
            left: `${p.left}vw`,
            width: `${p.size}px`,
            height: `${p.size * (p.shape === "circle" ? 1 : 1.6)}px`,
            backgroundColor: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            "--rotate-start": `${p.rotate}deg`,
            "--drift": `${p.drift}vw`,
          }}
        />
      ))}
    </div>
  );
}

const CONFETTI_CSS = `
@keyframes cc-confetti-fall {
  0%   { transform: translate3d(0, -10vh, 0) rotate(var(--rotate-start)); opacity: 1; }
  85%  { opacity: 1; }
  100% { transform: translate3d(var(--drift), 110vh, 0) rotate(calc(var(--rotate-start) + 720deg)); opacity: 0; }
}
.cc-confetti-piece {
  position: absolute;
  top: 0;
  display: block;
  animation-name: cc-confetti-fall;
  animation-timing-function: cubic-bezier(0.2, 0.6, 0.4, 1);
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
  will-change: transform, opacity;
}
.cc-confetti-circle { border-radius: 50%; }
@media (prefers-reduced-motion: reduce) {
  .cc-confetti-piece { display: none; }
}
`;
